import { useMemo } from "react";
import { Button, Descriptions, Image } from "antd";

import PropTypes from "prop-types";

import { colornames } from "color-name-list";

import { formatRateSummary } from "./RateFields";

const PreviewCardDetails = ({ carDetails, handleBackStep, handleAddCar, loading, t }) => {
    const colorSwatch = useMemo(
        () => (
            <div className="flex items-center gap-2">
                <div
                    className="h-4 w-4 shrink-0 rounded-full border border-gray-200"
                    style={{
                        backgroundColor:
                            colornames.find((color) => carDetails.color === color.name)?.hex || carDetails.color,
                    }}
                />
                <span>{carDetails.color}</span>
            </div>
        ),
        [carDetails.color],
    );

    const descriptionItems = useMemo(() => {
        const items = [
            {
                label: t("carName"),
                children: `${carDetails.company || carDetails.make} ${carDetails.model} ${carDetails.year}`,
                span: "filled",
            },
            {
                label: t("company"),
                children: carDetails.company || carDetails.make,
            },
            {
                label: t("model"),
                children: carDetails.model,
            },
            {
                label: t("year"),
                children: carDetails.year,
            },
            {
                label: t("bags"),
                children: carDetails.bags,
            },
            {
                label: t("seats"),
                children: carDetails.seats,
            },
            {
                label: t("color"),
                children: colorSwatch,
            },
            {
                label: t("category"),
                children: carDetails.categoryName,
            },
        ];

        if (carDetails.showCarPricing) {
            items.push(
                {
                    label: t("rates.setPrice"),
                    children: formatRateSummary(carDetails.rate, t),
                },
                {
                    label: t("rates.calenderDay"),
                    children: carDetails.calenderDay ? t("yes") : t("no"),
                },
            );
        }

        items.push(
            {
                label: t("plateNumber"),
                children: carDetails.plateNumber,
            },
            {
                label: t("vinNumberShort"),
                children: carDetails.vin,
            },
            {
                label: t("businessLocation"),
                children: carDetails.location,
            },
            {
                label: t("ezPassTagNumber"),
                children: carDetails.ezPassTagNumber || "N/A",
            },
            {
                label: t("worthValue"),
                children:
                    carDetails.worthValue !== null && carDetails.worthValue !== undefined
                        ? `$${carDetails.worthValue.toLocaleString()}`
                        : "N/A",
            },
        );

        return items;
    }, [carDetails, colorSwatch, t]);

    return (
        <div className="mt-4 h-full space-y-6 lg:mt-10">
            <div className="flex flex-col items-center justify-center gap-4 lg:flex-row">
                <div className="flex flex-wrap items-center justify-center gap-3 lg:gap-4">
                    {carDetails.images.map((image, index) => (
                        <Image
                            key={image.thumbUrl || index}
                            src={image.thumbUrl}
                            alt="car image"
                            className="object-cover"
                            width={120}
                            height={120}
                            rootClassName="lg:[&_.ant-image-img]:!h-[150px] lg:[&_.ant-image-img]:!w-[150px]"
                        />
                    ))}
                </div>
            </div>

            {/* Mobile: single-column stacked fields */}
            <div className="min-w-0 lg:hidden">
                <h3 className="mb-3 text-base font-bold text-[#121212]">{t("carInformation")}</h3>
                <div className="min-w-0 divide-y divide-[#f0f0f0] overflow-hidden rounded-lg border border-[#f0f0f0]">
                    {descriptionItems.map((item) => (
                        <div key={String(item.label)} className="flex min-w-0 flex-col gap-1 px-3 py-3">
                            <span className="text-xs font-medium text-[#acacb9]">{item.label}</span>
                            <div className="min-w-0 break-words text-sm text-[#121212]">{item.children}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Laptop / desktop: original bordered table */}
            <div className="hidden min-w-0 lg:block">
                <Descriptions bordered title={t("carInformation")} size="small" items={descriptionItems} />
            </div>

            <div className="mt-8 flex flex-col-reverse gap-3 lg:flex-row lg:justify-end lg:gap-4">
                <Button onClick={handleBackStep} className="w-full rounded-full lg:w-auto">
                    {t("back")}
                </Button>
                <Button
                    type="primary"
                    className="w-full rounded-full lg:w-auto"
                    onClick={handleAddCar}
                    loading={loading}
                    disabled={loading}>
                    {t("addCar")}
                </Button>
            </div>
        </div>
    );
};

PreviewCardDetails.propTypes = {
    carDetails: PropTypes.object.isRequired,
    handleBackStep: PropTypes.func.isRequired,
    handleAddCar: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    t: PropTypes.func.isRequired,
};

export default PreviewCardDetails;
